import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps } from "react";
import { InvoiceCardSection } from "../invoice-card-shell";
import { type FormatInvoiceMoney, isZeroMoney } from "./invoice-money";
import { INVOICE_CARD_COLORS, INVOICE_CARD_RADIUS } from "./tokens";

const LEDGER_MAX_WIDTH = 420;
const SWATCH_SIZE = 8;

type SplitBarSegment = "insurance" | "patient" | "paid";

const SplitBar = styled("div")<Theme, { "data-gap-below": number }>(
  ({ ...props }) => ({
    display: "flex",
    height: 8,
    borderRadius: INVOICE_CARD_RADIUS.pill,
    overflow: "hidden",
    backgroundColor: INVOICE_CARD_COLORS.divider,
    marginBottom: props["data-gap-below"],
  }),
);

const segmentColor = (theme: Theme, segment: SplitBarSegment): string =>
  ({
    insurance: theme.palette.brand.main,
    patient: theme.palette.primary.main,
    paid: theme.palette.success.main,
  })[segment];

const SplitBarSegmentBlock = styled("div")<
  Theme,
  { "data-segment": SplitBarSegment; "data-width": string }
>(({ theme, ...props }) => ({
  width: props["data-width"],
  backgroundColor: segmentColor(theme, props["data-segment"]),
}));

const Legend = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  fontWeight: 500,
  fontSize: 12.5,
  color: theme.palette.text.secondary,
  marginBottom: 14,
}));

const LegendItem = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const LegendSwatch = styled("span")<
  Theme,
  { "data-segment": SplitBarSegment | "outstanding" }
>(({ theme, ...props }) => ({
  width: SWATCH_SIZE,
  height: SWATCH_SIZE,
  borderRadius: INVOICE_CARD_RADIUS.swatch,
  backgroundColor:
    props["data-segment"] === "outstanding"
      ? INVOICE_CARD_COLORS.divider
      : segmentColor(theme, props["data-segment"]),
}));

const Ledger = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 6,
  maxWidth: LEDGER_MAX_WIDTH,
  marginLeft: "auto",
});

const LEDGER_ROW = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  fontWeight: 500,
  fontSize: 14,
} as const;

const LedgerRow = styled("div")<Theme>(({ theme }) => ({
  ...LEDGER_ROW,
  color: theme.palette.text.secondary,
}));

const LedgerValue = styled("span")<Theme>(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const CoverageRow = styled("div")<Theme>(({ theme }) => ({
  ...LEDGER_ROW,
  color: theme.palette.brand.main,
}));

const BalanceRow = styled("div")<Theme>({
  ...LEDGER_ROW,
  fontWeight: 600,
  color: INVOICE_CARD_COLORS.mutedBody,
  borderTop: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  paddingTop: 6,
});

const PaidRow = styled("div")<Theme>({
  ...LEDGER_ROW,
  color: INVOICE_CARD_COLORS.paidText,
});

const TotalRow = styled("div")<Theme>({
  ...LEDGER_ROW,
  alignItems: "baseline",
  borderTop: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  paddingTop: 8,
  marginTop: 2,
});

const totalColor = (theme: Theme, settled: boolean): string =>
  settled ? INVOICE_CARD_COLORS.paidText : theme.palette.primary.main;

const TotalLabel = styled("span")<Theme, { "data-settled": boolean }>(
  ({ theme, ...props }) => ({
    fontWeight: 700,
    fontSize: 15,
    color: totalColor(theme, props["data-settled"]),
  }),
);

const TotalValue = styled("span")<Theme, { "data-settled": boolean }>(
  ({ theme, ...props }) => ({
    fontWeight: 700,
    fontSize: 22,
    color: totalColor(theme, props["data-settled"]),
  }),
);

export type InvoiceCoverageEntry = {
  id: string;
  insurerName: string;
  amount: number;
};

const percentOf = (part: number, whole: number): string =>
  `${(whole > 0 ? Math.min(100, Math.max(0, (part / whole) * 100)) : 0).toFixed(
    2,
  )}%`;

export const InvoiceTotals: FC<
  {
    itemsBilled: number;
    coverage: InvoiceCoverageEntry[];
    patientBalance: number;
    formatMoney: FormatInvoiceMoney;
  } & (
    | { mode: "draft"; paidToDate?: never; outstanding?: never }
    | { mode: "finalised"; paidToDate: number; outstanding: number }
  ) &
    Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({
  itemsBilled,
  coverage,
  patientBalance,
  formatMoney,
  mode,
  paidToDate,
  outstanding,
  ...restProps
}) => {
  const coveredWidth = percentOf(itemsBilled - patientBalance, itemsBilled);
  const settled = mode === "finalised" && isZeroMoney(outstanding);

  return (
    <InvoiceCardSection {...restProps}>
      <SplitBar data-gap-below={mode === "draft" ? 12 : 10}>
        <SplitBarSegmentBlock
          data-segment="insurance"
          data-width={coveredWidth}
        />
        {mode === "draft" ? (
          <SplitBarSegmentBlock
            data-segment="patient"
            data-width={percentOf(patientBalance, itemsBilled)}
          />
        ) : (
          <SplitBarSegmentBlock
            data-segment="paid"
            data-width={percentOf(paidToDate, itemsBilled)}
          />
        )}
      </SplitBar>

      {mode === "finalised" && (
        <Legend>
          <LegendItem>
            <LegendSwatch data-segment="insurance" />
            Insurance
          </LegendItem>
          <LegendItem>
            <LegendSwatch data-segment="paid" />
            Paid by patient
          </LegendItem>
          <LegendItem>
            <LegendSwatch data-segment="outstanding" />
            Outstanding
          </LegendItem>
        </Legend>
      )}

      <Ledger>
        <LedgerRow>
          <span>Items billed</span>
          <LedgerValue>{formatMoney(itemsBilled)}</LedgerValue>
        </LedgerRow>
        {coverage.map((entry) => (
          <CoverageRow key={entry.id}>
            <span>Covered by {entry.insurerName}</span>
            <span>− {formatMoney(entry.amount)}</span>
          </CoverageRow>
        ))}

        {mode === "finalised" && (
          <>
            <BalanceRow>
              <span>Patient balance</span>
              <LedgerValue>{formatMoney(patientBalance)}</LedgerValue>
            </BalanceRow>
            <PaidRow>
              <span>Paid to date</span>
              <span>− {formatMoney(paidToDate)}</span>
            </PaidRow>
          </>
        )}

        <TotalRow>
          <TotalLabel data-settled={settled}>
            {mode === "draft"
              ? "Patient balance"
              : settled
                ? "Fully settled"
                : "Outstanding"}
          </TotalLabel>
          <TotalValue data-settled={settled}>
            {formatMoney(mode === "draft" ? patientBalance : outstanding)}
          </TotalValue>
        </TotalRow>
      </Ledger>
    </InvoiceCardSection>
  );
};
